import React, { useEffect, useMemo, useState } from "react";

import { cn } from "lib/utils";

export type TabItem = {
  key: string;
  label: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
};

export interface TabsProps {
  items: TabItem[];
  className?: string;
  defaultActiveKey?: string;
  activeKey?: string;
  onChange?: (key: string) => void;
  destroyOnHidden?: boolean;
}

const Tabs: React.FC<TabsProps> = ({
  items,
  className,
  defaultActiveKey,
  activeKey,
  onChange,
  destroyOnHidden = false,
}) => {
  const firstKey = useMemo(() => items[0]?.key ?? "", [items]);
  const [internalKey, setInternalKey] = useState<string>(
    defaultActiveKey ?? firstKey,
  );

  // Keep internal state in sync when the list of items or default changes
  useEffect(() => {
    if (defaultActiveKey !== undefined) {
      setInternalKey(defaultActiveKey);
    }
  }, [defaultActiveKey]);

  useEffect(() => {
    if (!items.some((item) => item.key === internalKey) && items.length) {
      setInternalKey(items[0].key);
    }
  }, [items, internalKey]);

  const currentKey = activeKey ?? internalKey;

  const handleSelect = (key: string, disabled?: boolean) => {
    if (disabled || key === currentKey) return;
    if (!activeKey) {
      setInternalKey(key);
    }
    onChange?.(key);
  };

  return (
    <div className={cn("w-full", className)}>
      <div
        className="flex flex-wrap gap-2 border-b border-slate-200 sm:gap-4"
        role="tablist"
        aria-orientation="horizontal"
        data-sentry-unmask
      >
        {items.map((item) => {
          const isActive = item.key === currentKey;
          return (
            <button
              type="button"
              key={item.key}
              disabled={item.disabled}
              onClick={() => handleSelect(item.key, item.disabled)}
              className={cn(
                "relative -mb-px inline-flex items-center border-b-2 border-transparent px-2 py-2 text-sm font-medium transition-colors focus-visible:outline-none",
                isActive
                  ? "border-primary text-primary"
                  : "text-slate-500 hover:text-slate-800",
                item.disabled && "cursor-not-allowed opacity-40",
              )}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tab-panel-${item.key}`}
            >
              <span className="truncate">{item.label}</span>
            </button>
          );
        })}
      </div>
      <div className="pt-4">
        {items.map((item) => {
          const isActive = item.key === currentKey;
          if (!isActive && destroyOnHidden) {
            return null;
          }

          return (
            <div
              key={item.key}
              role="tabpanel"
              hidden={!isActive}
              aria-hidden={!isActive}
              id={`tab-panel-${item.key}`}
            >
              {item.children}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Tabs;
